import React, { useCallback, useMemo, useRef } from 'react';
import { diff } from 'deep-object-diff';
import './wrestlers.css';
import useTeams from '@hooks/useTeams';
import MaterialTable, {
    materialTableHeight,
    TableActions,
} from '@components/molecules/MaterialTable';
import { Column, Options, SvgIconComponent } from '@material-table/core';
import FAB from '@atoms/FAB';
import { AthleteTeamMember, COACH_ROLE, STATUS } from '@type/db-types';
import { toast } from 'react-toastify';
import { updateUserAthlete } from '@modules/supabase/users';
import { TableActionData } from '@type/mui-types';
import { log } from '@utils/logging';
import { stringFromError } from '@utils/utils';
import useModalControl from '@hooks/useModalControl';
import GenderSelector from '@atoms/GenderSelector';
import { Grid2 as Grid, Typography } from '@mui/material';
import useUser from '@hooks/useUser';
import { updateTeamStatus } from '@modules/supabase/teams';
import { GENDER_LOOKUP, GRADE_LOOKUP } from '@utils/team-table-utils';
import { AGE_COLUMN, editableUserColumns, EXPERIENCE_COLUMN } from '@utils/user-table-utils';
import ActiveSinceSelector from '@atoms/ActiveSinceSelector';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GradeSelector from '@atoms/GradeSelector';

export default function Wrestlers() {
    const currentUser = useUser();
    const { setShowAddTeamMember } = useModalControl();
    const { athletes, refreshMembers, role, selectedTeam } = useTeams();
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();
    const { setShowImportModal } = useModalControl();

    const actions: TableActions<AthleteTeamMember> = useMemo(
        () => [
            {
                icon: UploadFileIcon as SvgIconComponent,
                tooltip: 'Import users',
                isFreeAction: true,
                onClick: () => setShowImportModal(true),
            },
        ],
        [setShowImportModal],
    );

    const successCallBack = useCallback(() => {
        // takes a minute for the updated info to trickle through
        // TODO: eventually we should hook this up to a watcher...
        toast.info('Updating wrestlers...', { autoClose: 1000 });
        setTimeout(() => refreshMembers(), 1000);
    }, [refreshMembers]);

    const onRowUpdate = useCallback(
        (newData: AthleteTeamMember, oldData?: AthleteTeamMember | undefined) => {
            const updatedFields: Partial<AthleteTeamMember> & TableActionData = diff(
                oldData || {},
                newData,
            );
            if ('tableData' in updatedFields) delete updatedFields.tableData;

            if (Object.keys(updatedFields).length) {
                return updateUserAthlete(
                    {
                        ...oldData,
                        ...updatedFields,
                    },
                    'update wrestler row',
                )
                    .then(successCallBack)
                    .catch((e) => log(`[Wrestlers:edit] ${stringFromError(e)}`));
            } else {
                toast.info('No changes detected');
                return Promise.resolve();
            }
        },
        [successCallBack],
    );

    const tableColumns = useMemo(
        (): Column<AthleteTeamMember>[] => [
            ...(editableUserColumns({
                userKey: 'id',
                hideRole: true,
                hideEmail: true,
                hideTeamStatus: true,
            }) as unknown as Column<AthleteTeamMember>[]),
            {
                editComponent: (props) => (
                    <GenderSelector user={props.rowData} refresh={refreshMembers} />
                ),
                field: 'gender',
                lookup: GENDER_LOOKUP,
                render: (user) => <GenderSelector user={user} refresh={refreshMembers} />,
                title: 'Gender',
            },
            AGE_COLUMN(),
            { title: 'Weight', field: 'weight', filtering: false },
            {
                editComponent: (props) => (
                    <GradeSelector user={props.rowData} refresh={refreshMembers} />
                ),
                field: 'grade',
                lookup: GRADE_LOOKUP,
                render: (user) => <GradeSelector user={user} refresh={refreshMembers} />,
                title: 'Grade',
            },
            EXPERIENCE_COLUMN(),
            {
                editComponent: (props) => (
                    <ActiveSinceSelector user={props.rowData} refresh={refreshMembers} />
                ),
                field: 'activeSince',
                filtering: false,
                render: (user) => <ActiveSinceSelector user={user} refresh={refreshMembers} />,
                title: 'Wrestling Since',
            },
        ],
        [refreshMembers],
    );

    const onRowDelete = async (oldData: AthleteTeamMember) => {
        if (currentUser.user?.id === oldData.userId) {
            return toast.error('You cannot remove yourself');
        }
        await toast
            .promise(updateTeamStatus(STATUS.REMOVED, oldData.membershipId), {
                pending: 'Removing wrestler...',
                success: 'Wrestler removed',
                error: 'Error removing wrestler',
            })
            .then(refreshMembers);
    };

    // if no team selected, show a message
    if (!selectedTeam) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: '90vh', width: '100%' }}
            >
                <Grid>
                    <Typography variant="h5">Please select a team to view wrestlers</Typography>
                </Grid>
            </Grid>
        );
    }

    if (!role || !COACH_ROLE.includes(role)) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ height: '90vh' }}>
                <Grid>
                    <Typography variant="h4">You are not authorized to view this page</Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            {/* <div className="main-container-inner p-4"> */}
            {/* <CopyLinkCard
                    cardTitle={'Registration link'}
                    cardDescription={
                        'Share this link with your wrestlers to register them to your team.'
                    }
                    // copyLink={`${window.location.origin}/register-wrestler/${user.schoolId}`}
                    copyLink={`${window.location.origin}/register-wrestler`}
                /> */}

            <MaterialTable<AthleteTeamMember>
                tableRef={tableRef}
                actions={actions}
                columns={tableColumns}
                data={athletes || []}
                editable={{
                    isEditable: () => true,
                    onRowUpdate,
                    onRowDelete,
                }}
                localization={{
                    toolbar: {
                        nRowsSelected: (number) =>
                            `${number} wrestler${number === 1 ? '' : 's'} selected`,
                    },
                }}
                loading={athletes === undefined}
                style={{ boxShadow: 'none' }}
                options={TABLE_OPTIONS}
                // onRowClick={(e, rowData) => {
                //     setSelecteduser(rowData);
                // }}
                tableId="Wrestlers"
                title="Wrestlers"
            />
            {/* </div> */}

            <FAB onClick={() => setShowAddTeamMember(true)} toolTip="Add wrestler" />
        </>
    );
}

const TABLE_OPTIONS: Options<AthleteTeamMember> = {
    actionsColumnIndex: -1,
    maxBodyHeight: `${materialTableHeight - 7}vh`,
    minBodyHeight: `${materialTableHeight - 7}vh`,
};
